// CTF dynamic questions — client view of an exercise's generated question set (poc spec
// 2026-10-13-ctf-dynamic-questions-design.md). Pure, no React.
//
// A Dynamic CTF scenario has no questions at build time: each exercise gets its own auto-checked set,
// generated when the exercise is set up and ready by the start. Staff see its status; players never
// see the status internals or the answer key — at most a friendly "being prepared" lobby state.
//
// The server summary may arrive as `dynamicQuestions` (this client's name) or `freshQuestions` (the
// design blueprint's name), on exercise rows, the composed session (staff only) and the live screen
// model (`host.dynamicQuestions`). Blueprint statuses map onto the user's model: there is NO silent
// fallback to a reviewed set, so "fallback" / "discarded_late" read as held.

export const DYNAMIC_STATUSES = ["pending", "generating", "ready", "failed", "held"]

const STATUS_ALIASES = { queued: "pending", claimed: "generating", running: "generating", fallback: "held", discarded_late: "held", locked: "ready" }

export const normalizeDynamicStatus = (raw) => {
  const s = String(raw || "").toLowerCase()
  if (DYNAMIC_STATUSES.includes(s)) return s
  return STATUS_ALIASES[s] || "pending"
}

/** The staff summary from a row / session / host model, or null when this isn't a dynamic exercise. */
export const dynamicQuestionsOf = (obj) => {
  const raw = obj?.dynamicQuestions || obj?.freshQuestions || null
  if (!raw || typeof raw !== "object") return null
  const status = normalizeDynamicStatus(raw.status)
  const lastError = raw.lastError && typeof raw.lastError === "object" ? raw.lastError : null
  const count = Number(raw.questionCount ?? raw.stats?.kept ?? raw.count) || 0
  return {
    status,
    questionCount: count,
    roundCount: Number(raw.roundCount ?? raw.stats?.rounds) || 0,
    readyAt: raw.readyAt || null,
    generation: raw.generation ?? null,
    // Why a set isn't usable yet (held / failed) — a plain sentence for the host.
    reason: raw.heldReason || raw.reason || lastError?.message || null,
    // The server decides; when it doesn't say, regenerate is allowed before the start and retry when stuck.
    canRegenerate: raw.canRegenerate ?? (status === "ready" || status === "failed" || status === "held"),
    canRetry: raw.canRetry ?? (status === "failed" || status === "held"),
    locked: raw.locked === true,
  }
}

export const DYNAMIC_STATUS_LABELS = {
  pending: "Queued",
  generating: "Generating…",
  ready: "Ready",
  failed: "Couldn't generate",
  held: "Held — needs attention",
}

// Chip tone, one of the shared .ct-dyn-chip modifiers.
export const dynamicStatusTone = (status) =>
  ({ ready: "ok", generating: "busy", pending: "busy", failed: "bad", held: "warn" })[normalizeDynamicStatus(status)] || "busy"

/** One line for an exercise row / detail: "Questions: Ready · 12 questions". */
export const dynamicQuestionsLine = (dq) => {
  if (!dq) return null
  const label = DYNAMIC_STATUS_LABELS[dq.status]
  const count = dq.status === "ready" && dq.questionCount ? ` · ${dq.questionCount} question${dq.questionCount === 1 ? "" : "s"}` : ""
  return `Questions: ${label}${count}`
}

/** Why the host can't start yet, or null when the set is ready (or the exercise isn't dynamic). */
export const dynamicStartBlockedReason = (dq) => {
  if (!dq || dq.status === "ready") return null
  if (dq.status === "failed" || dq.status === "held") return `This exercise's questions aren't ready: ${dq.reason || "generation didn't finish"}. Retry to try again.`
  return "This exercise's questions are still being generated — Start unlocks when they're ready."
}

/** A scenario (from the scenario list / wizard) whose questions are generated per exercise. */
export const scenarioIsDynamicCtf = (scenario) => {
  const info = scenario?.info || {}
  const mode = String(info["CTF Question Mode"] ?? info.ctfQuestionMode ?? scenario?.ctfQuestionMode ?? "").toLowerCase()
  return mode === "dynamic" || mode === "fresh"
}

/** Player lobby: the event is waiting on its questions (no internals — just "being prepared"). */
export const playerLobbyPreparing = (model) => !!(model && (model.lobby?.preparing === true || model.preparing === true))
